// Helpers for classifying screening-submission (axios) errors so the UI can
// decide whether to retry and which message to show.

// Client-side timeout for submission requests. Without an explicit timeout
// axios waits indefinitely, so a stalled request would never surface as an
// error the UI could retry or report.
export const SUBMISSION_TIMEOUT_MS = 30000;

// A request that exceeded the client timeout. Axios aborts timed-out requests
// with code 'ECONNABORTED' (and, in some environments, 'ETIMEDOUT').
export function isTimeoutError(error) {
  if (!error) {
    return false;
  }

  const code = error.code;
  if (code === 'ECONNABORTED' || code === 'ETIMEDOUT') {
    return true;
  }

  // Fallback: axios timeout messages read "timeout of Xms exceeded".
  return typeof error.message === 'string'
    && error.message.toLowerCase().includes('timeout');
}

// A server-side (5xx) failure.
export function isServerError(error) {
  const status = error?.response?.status;
  return typeof status === 'number' && status >= 500;
}

// We retry once on transient failures: server errors or timeouts.
export function isRetryableError(error) {
  return isServerError(error) || isTimeoutError(error);
}

// Runs the submission and, on a transient failure (server error or timeout),
// retries exactly once before letting the error propagate. Non-retryable
// errors are rethrown immediately.
export async function submitWithRetry(submitFn) {
  try {
    return await submitFn();
  } catch (error) {
    if (!isRetryableError(error)) {
      throw error;
    }
    return submitFn();
  }
}
